/**
 * Desktop mega menu promo hydration: clones markup from #mega-menu-promo-store into
 * .mega-menu__promo--dynamic slots when header-menu dispatches mega-menu:activated.
 * Listeners attach after first user interaction on the header (lazy arm).
 */

export const MEGA_MENU_ACTIVATED_EVENT = 'mega-menu:activated';

/** @returns {HTMLElement | null} */
function getHeaderComponent() {
  return document.getElementById('header-component');
}

function isPromoFeatureEnabled() {
  const header = getHeaderComponent();
  return Boolean(header) && header?.dataset.megaMenuPromoEnabled !== 'false';
}

/** @returns {HTMLElement | null} */
function getPromoStore() {
  /** @type {HTMLElement[]} */
  const stores = [...document.querySelectorAll('#mega-menu-promo-store')].filter(
    (el) => el instanceof HTMLElement,
  );
  if (stores.length === 0) return null;

  const populated = stores.find((el) => {
    const count = parseInt(el.dataset.promoBlockCount ?? '0', 10);
    return !Number.isNaN(count) && count > 0;
  });

  const store = populated ?? stores[stores.length - 1];
  return store ?? null;
}

/**
 * @param {HTMLElement | null | undefined} listEl
 * @param {boolean} shrink
 */
function adjustMegaMenuListForPromo(listEl, shrink) {
  // Keep menu column sizing controlled by Liquid/CSS.
  // We only hydrate promo content; do not mutate --menu-columns-* in JS.
  if (!(listEl instanceof HTMLElement)) return;
  if (!shrink) delete listEl.dataset.promoColsAdjusted;
}

/**
 * @param {HTMLElement} submenu
 */
function hydrateMegaMenuPromo(submenu) {
  if (!isPromoFeatureEnabled()) return;

  const store = getPromoStore();
  const promoSlot = submenu.querySelector('.mega-menu__promo--dynamic, .menu-drawer__promo--dynamic');
  if (!(promoSlot instanceof HTMLElement)) return;

  const grid = submenu.querySelector('.mega-menu__grid');
  const gridId = grid instanceof HTMLElement ? grid.dataset.menuGridId : null;
  const list = gridId ? submenu.querySelector(`[data-menu-list-id="${gridId}"]`) : null;
  const listElement = list instanceof HTMLElement ? list : null;

  if (!store) {
    promoSlot.toggleAttribute('hidden', true);
    adjustMegaMenuListForPromo(listElement, false);
    return;
  }

  const keys = [promoSlot.dataset.promoSlot, promoSlot.dataset.promoSlotAlt].filter(Boolean);
  const storeItem = [...store.querySelectorAll('[data-promo-menu-key]')].find((el) =>
    keys.includes(el.getAttribute('data-promo-menu-key') ?? ''),
  );

  const targetList = promoSlot.querySelector('.mega-menu__promo-list');
  if (!(targetList instanceof HTMLElement)) return;

  const menuKey = storeItem?.getAttribute('data-promo-menu-key') ?? '';

  if (storeItem) {
    const sourceList = storeItem.querySelector('.mega-menu__promo-list');
    if (!(sourceList instanceof HTMLElement)) return;

    promoSlot.toggleAttribute('hidden', false);

    const alreadyHydrated = promoSlot.dataset.promoHydrated === menuKey;
    if (!alreadyHydrated) {
      targetList.replaceChildren(...sourceList.cloneNode(true).childNodes);
      targetList.dataset.promoMountedKey = menuKey;
      promoSlot.dataset.promoHydrated = menuKey;
    }

    adjustMegaMenuListForPromo(listElement, true);
  } else {
    promoSlot.toggleAttribute('hidden', true);
    adjustMegaMenuListForPromo(listElement, false);
  }
}

/**
 * @param {Event} event
 */
function onMegaMenuActivated(event) {
  if (!(event instanceof CustomEvent)) return;
  const submenu = event.detail?.submenu;
  if (!(submenu instanceof HTMLElement)) return;
  hydrateMegaMenuPromo(submenu);
}

let listenersArmed = false;

function armMegaMenuPromoListeners() {
  if (listenersArmed) return;
  listenersArmed = true;
  document.addEventListener(MEGA_MENU_ACTIVATED_EVENT, onMegaMenuActivated);
  document.addEventListener('pointerover', onHeaderPointerOver, true);
  document.addEventListener('toggle', onDrawerDetailsToggle, true);
  document.addEventListener('click', onDrawerMainClick, true);
}

function initMegaMenuPromo() {
  if (!isPromoFeatureEnabled()) return;

  const header = getHeaderComponent();
  if (!header) return;

  const armFromInteraction = () => armMegaMenuPromoListeners();

  header.addEventListener('pointerdown', armFromInteraction, { once: true, capture: true });
  header.addEventListener('focusin', armFromInteraction, { once: true, capture: true });
  header.addEventListener('pointerenter', armFromInteraction, { once: true });

  document.addEventListener(
    MEGA_MENU_ACTIVATED_EVENT,
    (event) => {
      armMegaMenuPromoListeners();
      onMegaMenuActivated(event);
    },
    { capture: true, once: true },
  );
}

/**
 * Fallback hydration path in case custom event doesn't fire in some editor states.
 * @param {Event} event
 */
function onHeaderPointerOver(event) {
  if (!(event.target instanceof Element)) return;

  const menuItem = event.target.closest('.menu-list__list-item');
  if (!(menuItem instanceof HTMLElement)) return;

  const submenu = menuItem.querySelector('[ref="submenu[]"]');
  if (!(submenu instanceof HTMLElement)) return;

  hydrateMegaMenuPromo(submenu);
}

/**
 * Mobile drawer fallback for accordion/details menus.
 * @param {Event} event
 */
function onDrawerDetailsToggle(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.open) return;
  if (!details.id.startsWith('Details-menu-drawer-')) return;

  hydrateMegaMenuPromo(details);
}

/**
 * Mobile drawer fallback for flat menu branches.
 * @param {Event} event
 */
function onDrawerMainClick(event) {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('.menu-drawer__menu-item--mainlist');
  if (!(trigger instanceof HTMLElement)) return;

  const listItem = trigger.closest('.menu-drawer__list-item');
  if (!(listItem instanceof HTMLElement)) return;

  hydrateMegaMenuPromo(listItem);
}

/** Reset slots when the promo section re-renders in the theme editor. */
function onPromoSectionLoad(/** @type {Event} */ event) {
  const root = event.target;
  if (!(root instanceof HTMLElement)) return;
  if (!root.querySelector('#mega-menu-promo-store')) return;

  document.querySelectorAll('.mega-menu__promo--dynamic, .menu-drawer__promo--dynamic').forEach((slot) => {
    if (!(slot instanceof HTMLElement)) return;
    delete slot.dataset.promoHydrated;
    const listEl = slot.querySelector('.mega-menu__promo-list');
    if (listEl instanceof HTMLElement) {
      listEl.replaceChildren();
      delete listEl.dataset.promoMountedKey;
    }
  });
  document.querySelectorAll('[data-promo-cols-adjusted]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.style.removeProperty('--menu-columns-desktop');
    el.style.removeProperty('--menu-columns-tablet');
    delete el.dataset.promoColsAdjusted;
  });
}

document.addEventListener('shopify:section:load', onPromoSectionLoad);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaMenuPromo, { once: true });
} else {
  initMegaMenuPromo();
}
